import {
  createGoal,
  CreateGoalMutation,
  CreateGoalMutationVariables,
  deleteGoal,
  DeleteGoalMutation,
  DeleteGoalMutationVariables,
  Goal as GoalGraphql,
  graphqlCall,
  GraphQLResult,
  updateGoal,
  UpdateGoalMutation,
  UpdateGoalMutationVariables,
} from '@akin-home/amplify';
import { Goal } from '@akin-home/goals';
import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  goalModelToCreateGraphql,
  goalModelToUpdateGraphql,
} from '../functions';
import { goal_query_keys } from './query_keys';

export const updateGoalMutation = (params: UpdateGoalMutationVariables) =>
  graphqlCall<UpdateGoalMutation>(updateGoal, params);

export const createGoalMutation = (params: CreateGoalMutationVariables) =>
  graphqlCall<CreateGoalMutation>(createGoal, params);

export const deleteGoalMutation = (params: DeleteGoalMutationVariables) =>
  graphqlCall<DeleteGoalMutation>(deleteGoal, params);

export const useUpdateGoalMutation = (options?: any) =>
  useMutation(updateGoalMutation, options);

export const useCreateGoalMutation = (options?: any) =>
  useMutation(createGoalMutation, options);
  
export const useDeleteGoalMutation = (options?: any) =>
  useMutation(deleteGoalMutation, options);

export const useUpdateGoal = (options?: any) => {
  const queryClient = useQueryClient();
  const update = useUpdateGoalMutation({
    onSuccess: ({
      data: { updateGoal },
    }: GraphQLResult<UpdateGoalMutation>) => {
      queryClient.setQueriesData<GoalGraphql[]>(
        goal_query_keys.lists(),
        (data = []) => {
          return data.map((i) =>
            i.id === updateGoal?.id
              ? { ...i, ...(updateGoal as GoalGraphql) }
              : i
          );
        }
      );

      queryClient.setQueriesData<GoalGraphql | undefined>(
        goal_query_keys.detail(updateGoal?.id),
        (data) => {
          return data && updateGoal
            ? { ...data, ...(updateGoal as GoalGraphql) }
            : data;
        }
      );
    },
  });

  return useCallback(
    (goal: Goal) =>
      update.mutateAsync({ input: goalModelToUpdateGraphql(goal) }),
    [update]
  );
};

export const useCreateGoal = (options?: any) => {
  const queryClient = useQueryClient();

  const create = useCreateGoalMutation({
    onSuccess: (d: GraphQLResult<CreateGoalMutation>) => {
      queryClient.setQueriesData<GoalGraphql[]>(
        goal_query_keys.lists(),
        (data = []) => {
          return [...data, d.data.createGoal as GoalGraphql];
        }
      );
    },
  });

  return useCallback(
    (goal: Goal) =>
      create.mutateAsync({ input: goalModelToCreateGraphql(goal) }),
    [create]
  );
};

export const useDeleteGoal = (options?: any) => {
  const queryClient = useQueryClient();
  const remove = useDeleteGoalMutation({
    onSuccess: ({
      data: { deleteGoal },
    }: GraphQLResult<DeleteGoalMutation>) => {
      queryClient.setQueriesData<GoalGraphql[]>(
        goal_query_keys.lists(),
        (data = []) => data.filter((d) => d.id !== deleteGoal?.id)
      );

      queryClient.removeQueries(goal_query_keys.detail(deleteGoal?.id));
    },
  });
  return useCallback(
    (id: string) => remove.mutateAsync({ input: { id } }),
    [remove]
  );
};

export const useUpdateGoalCompletion = () => {
  const queryClient = useQueryClient();

  const update = useUpdateGoalMutation({
    onMutate: async (vars: { input: { id: string; completions: string } }) => {
      queryClient.setQueriesData<GoalGraphql[]>(
        goal_query_keys.lists(),
        (data = []) => {
          if (!data) return [];
          const id = vars.input.id;
          const item = data.find((i) => i.id === id);
          if (!item) return data;
          return data.map((i) =>
            i.id === item.id ? { ...i, ...vars.input } : i
          );
        }
      );

      queryClient.setQueriesData<GoalGraphql | undefined>(
        goal_query_keys.detail(vars.input.id),
        (data) => {
          return data ? { ...data, completions: vars.input.completions } : data;
        }
      );

      queryClient.setQueriesData<
        { pages: { items: GoalGraphql[] }[] } | undefined
      >(goal_query_keys.all_pages(), (data) => {
        return data
          ? {
              pages: data.pages.map((page) => ({
                ...page,
                items: page.items.map((item) => {
                  if (item.id === vars.input.id) {
                    return {
                      ...item,
                      ...vars.input,
                    };
                  }
                  return item;
                }),
              })),
            }
          : undefined;
      });
    },
  });

  return useCallback(
    (goal: Goal) => {
      update.mutateAsync({
        input: { id: goal.id, completions: JSON.stringify(goal.completions) },
      });
    },
    [update]
  );
};
